/**
 * FlowStorage: load / save / clear a serializable PostAJob snapshot.
 *
 * Default: localStorage (survives tab close / "resume later").
 * Tests: inject createMemoryFlowStorage().
 * Later: swap for API/session draft storage without changing step screens.
 */

export const POST_A_JOB_FLOW_STORAGE_KEY = 'postAJobFlow';
export const POST_A_JOB_FLOW_COMPLETED_KEY = 'postAJobFlowCompleted';

/**
 * Mark the native post flow as completed so a later mid-flow URL / browser Back
 * does not treat the previous draft as resumable.
 * @returns {void}
 */
export const markPostAJobFlowCompleted = () => {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    if (!window.sessionStorage) {
      return;
    }
    window.sessionStorage.setItem(POST_A_JOB_FLOW_COMPLETED_KEY, '1');
  } catch (e) {
    // Ignore private-mode failures.
  }
};

/**
 * @returns {boolean} true when a completion was recorded (and cleared).
 */
export const consumePostAJobFlowCompleted = () => {
  if (typeof window === 'undefined') {
    return false;
  }
  try {
    if (!window.sessionStorage) {
      return false;
    }
    const value = window.sessionStorage.getItem(POST_A_JOB_FLOW_COMPLETED_KEY);
    if (!value) {
      return false;
    }
    window.sessionStorage.removeItem(POST_A_JOB_FLOW_COMPLETED_KEY);
    return true;
  } catch (e) {
    return false;
  }
};

export const createMemoryFlowStorage = () => {
  let snapshot = null;

  return {
    load: () => snapshot,
    save: (next) => {
      snapshot = next;
    },
    clear: () => {
      snapshot = null;
    },
  };
};

export const createLocalStorageFlowStorage = (
  key = POST_A_JOB_FLOW_STORAGE_KEY
) => ({
  load: () => {
    if (typeof window === 'undefined' || !window.localStorage) {
      return null;
    }
    try {
      const raw = window.localStorage.getItem(key);
      if (!raw) {
        return null;
      }
      return JSON.parse(raw);
    } catch (e) {
      return null;
    }
  },
  save: (snapshot) => {
    if (typeof window === 'undefined' || !window.localStorage) {
      return;
    }
    try {
      window.localStorage.setItem(key, JSON.stringify(snapshot));
    } catch (e) {
      // Ignore quota / private-mode failures.
    }
  },
  clear: () => {
    if (typeof window === 'undefined' || !window.localStorage) {
      return;
    }
    try {
      window.localStorage.removeItem(key);
    } catch (e) {
      // Ignore private-mode failures.
    }
  },
});
